import {
  type Collection,
  type CountableApiResponse,
  type CreateFileDto,
  CreateFileFormField,
  type CreateFileQuery,
  type FileType,
  type SerializedFile,
  type UpdateFileDto,
} from '@enmon/commons/lib/enmon';
import { RotateDirection } from '@enmon/commons/lib/enmon/files';
import type { Pagination } from 'types/backendGateway/pagination';
import { BaseApiClient } from '../baseApiClient';

export interface FilesFilter extends Pagination {
  invoices?: string[];
  manualReadings?: string[];
  emissionRecords?: string[];
  customEmissionFactorValidities?: string[];
  customEmissionFactors?: string[];
  meters?: string[];
  supplyPoints?: string[];
  customers?: string[];
  establishments?: string[];
  floorPlans?: string[];
  collections?: Collection[];
  revisions?: string[];
  fileTypes?: FileType[];
}

export type GetFilesArg = FilesFilter;
export type GetFileLinkArg = SerializedFile['_id'];
export type GetFileThumbnailArg = SerializedFile['_id'];

export type CreateFilesArg = {
  query: CreateFileQuery;
  dto?: CreateFileDto;
  files: File[];
};
export type UpdateFileArg = {
  id: SerializedFile['_id'];
  payload: UpdateFileDto;
};
export type DeleteFileArg = SerializedFile['_id'];

export type RotateFileArg = {
  fileId: string;
  direction: RotateDirection;
};

export type GetFilesResponse = CountableApiResponse<SerializedFile[]> | null;
export type GetFileLinkResponse = { link: string } | null;
export type GetFileThumbnailResponse = { link: string } | null;

export type CreateFilesResponse = SerializedFile[] | null;
export type UpdateFileResponse = SerializedFile | null;
export type DeleteFileResponse = void | null;

export class BackendGatewayFilesApiClient extends BaseApiClient {
  constructor(jwt: string) {
    super('enmon-tools/files', undefined, jwt);
  }

  public async get(filter: GetFilesArg): Promise<GetFilesResponse> {
    const response = await this.axios.get<GetFilesResponse>(``, {
      params: filter,
    });

    return response.data;
  }

  public async getOne(id: GetFileLinkArg): Promise<SerializedFile> {
    const response = await this.axios.get<SerializedFile>(`${id}`);

    return response.data;
  }

  public async getFileLink(fileId: GetFileLinkArg): Promise<GetFileLinkResponse> {
    const response = await this.axios.get<GetFileLinkResponse>(`${fileId}/link`);

    return response.data;
  }

  public async getFileThumbnail(fileId: GetFileThumbnailArg): Promise<GetFileThumbnailResponse> {
    const response = await this.axios.get<GetFileThumbnailResponse>(`${fileId}/thumbnail-link`);

    return response.data;
  }

  public async create({ query, dto, files }: CreateFilesArg): Promise<CreateFilesResponse> {
    const formData = new FormData();
    files.forEach(file => {
      formData.append(CreateFileFormField.File, file);
    });

    if (dto) {
      formData.set(CreateFileFormField.Data, JSON.stringify(dto));
    }

    const response = await this.axios.post('', formData, {
      params: query,
    });

    return response.data;
  }

  public update({ id, payload }: UpdateFileArg): Promise<UpdateFileResponse> {
    return this.axios.patch(`${id}`, payload);
  }

  public delete(id: DeleteFileArg): Promise<DeleteFileResponse> {
    return this.axios.delete(`${id}`);
  }

  public async rotate({ fileId, direction }: RotateFileArg): Promise<SerializedFile> {
    const response = await this.axios.post<SerializedFile>(`/${fileId}/rotate`, { direction });
    return response.data;
  }
ß
  public async resetRotation( fileId: string): Promise<SerializedFile> {
    const response = await this.axios.post<SerializedFile>(`/${fileId}/rotate`, { direction: undefined });
    return response.data;
  }
}
